import MorwukImage from '@/assets/img/morwük.svg?react';
import { convertToErrorString } from '@/helpers/form/useErrorHandler';
import { Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { Button } from '../Button/Button';

import styles from './Loading.module.scss';

type ErrorBoundaryFallbackProps = {
  error: unknown;
  componentStack: string | null;
  eventId: string | null;
  resetError(): void;
};
export const errorBoundaryFallbackFunction = (props: ErrorBoundaryFallbackProps) => (
  <ErrorBoundaryFallback {...props} />
);

/**
 * A plain-text dump of everything useful for diagnosing a crash from a user's report: the message,
 * the stack, where in the tree it happened, and which page/browser it was. Users copy this and send
 * it over, which is how failures reach us without a live error tracker.
 */
const buildErrorReport = (props: ErrorBoundaryFallbackProps): string => {
  const { error, componentStack, eventId } = props;
  const lines = [
    'Seger error report',
    `Time: ${new Date().toISOString()}`,
    `URL: ${window.location.href}`,
    `User agent: ${navigator.userAgent}`,
    ...(eventId ? [`Event id: ${eventId}`] : []),
    `Error: ${convertToErrorString(error)}`,
    ...(error instanceof Error && error.stack ? ['', 'Stack:', error.stack] : []),
    ...(componentStack ? ['', 'Component stack:', componentStack] : []),
  ];
  return lines.join('\n');
};

export const ErrorBoundaryFallback = (props: ErrorBoundaryFallbackProps) => {
  const location = useLocation();
  const i18n = useTranslation();
  const [initialLocation] = useState(location);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (initialLocation !== location) {
      props.resetError();
    }
  }, [initialLocation, location]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildErrorReport(props));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be unavailable (permissions, insecure context); nothing else to do here.
    }
  };

  return (
    <div className={styles.errorBoundary}>
      <div className={styles.flexLoadingData} data-test-id='loading-error'>
        <Stack
          sx={{
            alignItems: 'center',
          }}
        >
          <MorwukImage className={styles.errorBoundaryMorwukImage} />
          <div className={styles.errorBoundaryTitle}>{convertToErrorString(props.error)}</div>
          <div>{i18n.t('Suspense.ReloadPrompt')}</div>
          <Stack direction='row' spacing={1}>
            <Button
              onClick={() => window.location.reload()}
              color={'secondary'}
              variant={'outlined'}
            >
              {i18n.t('Suspense.Reload')}
            </Button>
            <Button onClick={handleCopy} color={'secondary'} variant={'text'}>
              {i18n.t(copied ? 'Suspense.ErrorDetailsCopied' : 'Suspense.CopyErrorDetails')}
            </Button>
          </Stack>
        </Stack>
      </div>
    </div>
  );
};
